import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      if (data.user.role === "admin") navigate("/admin");
      else if (data.user.status !== "approved") navigate("/pending");
      else navigate("/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Sign in"
      subtitle="Admins and students sign in here with your email and password."
      side="Examinations, reimagined."
      image="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=2000"
    >
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        {error && (
          <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 px-3 py-2" data-testid="login-error">
            {error}
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" placeholder="you@institution.edu" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" placeholder="••••••••" />
        </div>
        <Button type="submit" className="w-full rounded-sm" disabled={loading} data-testid="login-submit">
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Sign in
        </Button>
      </form>
      <div className="mt-6 text-sm text-muted-foreground space-y-1">
        <div>
          New student?{" "}
          <Link to="/register" className="text-primary font-medium hover:underline" data-testid="link-register-student">
            Create an account
          </Link>
        </div>
        <div>
          Are you an administrator?{" "}
          <Link to="/register/admin" className="text-primary font-medium hover:underline" data-testid="link-register-admin">
            Register as admin
          </Link>
        </div>
      </div>
    </AuthShell>
  );
}
